import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Paper,
  Slide,
  Fade,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Pagination,
  FormControlLabel,
  Switch,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Badge
} from '@mui/material';
import {
  Article as ArticleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  DateRange as DateIcon,
  Link as LinkIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  Science as ScienceIcon,
  LibraryBooks as LibraryBooksIcon,
  Bookmark as BookmarkIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Sort as SortIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  PublishedWithChangesRounded
} from '@mui/icons-material';
import NavigationMenu from '../navigation/NavigationMenu';
import { APIPublishedWorkList, APIResearcherList } from '../../utilities/APIS';
import { headers, BASE_URL } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const PDFViewer = ({ open, onClose, pdfUrl, title = "PDF Document" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '70vh' }}
            title={title}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const Publications = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showToast = useCustomToast();
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [researchersData, setResearchersData] = useState([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterAndSortPublications();
  }, [publications, searchTerm, selectedType, selectedStatus, selectedYear, sortBy]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [publicationsRes, researchersRes] = await Promise.all([
        axios.get(APIPublishedWorkList, { headers }),
        axios.get(APIResearcherList, { headers })
      ]);

      const publicationsData = Array.isArray(publicationsRes.data.data) ? publicationsRes.data.data : [];
      const researchersData = Array.isArray(researchersRes.data.data) ? researchersRes.data.data : [];
      
      setPublications(publicationsData);
      setFilteredPublications(publicationsData);
      setResearchersData(researchersData);
    } catch (error) {
      let errorMessage = "Failed to load publications";
      if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPublications = () => {
    let filtered = [...publications];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pub =>
        pub.title?.toLowerCase().includes(term) ||
        pub.author_name?.toLowerCase().includes(term) ||
        pub.publisher?.toLowerCase().includes(term) ||
        pub.abstract?.toLowerCase().includes(term) ||
        pub.doi?.toLowerCase().includes(term)
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(pub => pub.publication_type === selectedType);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(pub => pub.status === selectedStatus);
    }

    // Year filter
    if (selectedYear !== 'all') {
      filtered = filtered.filter(pub => pub.publication_year === parseInt(selectedYear));
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.publication_date) - new Date(a.publication_date);
        case 'oldest':
          return new Date(a.publication_date) - new Date(b.publication_date);
        case 'year-desc':
          return b.publication_year - a.publication_year;
        case 'year-asc':
          return a.publication_year - b.publication_year;
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'author-asc':
          return (a.author_name || '').localeCompare(b.author_name || '');
        case 'author-desc':
          return (b.author_name || '').localeCompare(a.author_name || '');
        default:
          return 0;
      }
    });

    setFilteredPublications(filtered);
    setCurrentPage(1);
  };

  const handleViewPdf = (fileName) => {
    if (fileName) {
      const pdfUrl = `${BASE_URL}${fileName}`;
      setSelectedPdfUrl(pdfUrl);
      setPdfDialogOpen(true);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const getPublicationTypeIcon = (type) => {
    switch(type) {
      case 'journal': return <MenuBookIcon />;
      case 'conference': return <ScienceIcon />;
      case 'book': return <LibraryBooksIcon />;
      case 'chapter': return <BookmarkIcon />;
      case 'thesis': return <SchoolIcon />;
      default: return <ArticleIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'published': return 'success';
      case 'in_press': return 'warning';
      case 'submitted': return 'info';
      default: return 'default';
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedYear('all');
    setSortBy('newest');
  };

  const getUniqueYears = () => {
    const years = publications.map(pub => pub.publication_year).filter(Boolean);
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const getUniqueTypes = () => {
    const types = publications.map(pub => pub.publication_type).filter(Boolean);
    return [...new Set(types)];
  };

  const getUniqueStatuses = () => {
    const statuses = publications.map(pub => pub.status).filter(Boolean);
    return [...new Set(statuses)];
  };

  // Pagination
  const totalPages = Math.ceil(filteredPublications.length / itemsPerPage);
  const paginatedPublications = filteredPublications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderPublicationCard = (pub, index) => (
    <Fade in={true} timeout={index * 100}>
      <Card sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6
        }
      }}>
        <CardContent sx={{ flexGrow: 1 }}>
          {/* Header Chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={getPublicationTypeIcon(pub.publication_type)}
              label={pub.publication_type?.charAt(0).toUpperCase() + pub.publication_type?.slice(1)}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<DateIcon />}
              label={pub.publication_year}
              size="small"
              variant="outlined"
            />
            <Chip
              label={pub.status}
              size="small"
              color={getStatusColor(pub.status)}
            />
          </Box>

          {/* Title */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {pub.title}
          </Typography>

          {/* Authors */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {pub.author_name}
            </Typography>
          </Box>

          {/* Publisher */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <PublishedWithChangesRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {pub.publisher}
            </Typography>
          </Box>

          {/* DOI/ISSN */}
          {(pub.doi || pub.issn) && (
            <Box sx={{ mt: 1 }}>
              {pub.doi && (
                <Typography variant="caption" color="primary" display="block">
                  DOI: {pub.doi}
                </Typography>
              )}
              {pub.issn && (
                <Typography variant="caption" color="primary" display="block">
                  ISSN/ISBN: {pub.issn}
                </Typography>
              )}
            </Box>
          )}

          {/* Abstract Preview */}
          {pub.abstract && (
            <Typography variant="body2" color="text.secondary" sx={{ 
              mt: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {pub.abstract}
            </Typography>
          )}
        </CardContent>

        <Divider />

        <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {pub.file_name && (
              <Tooltip title="View PDF">
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => handleViewPdf(pub.file_name)}
                >
                  <PdfIcon />
                </IconButton>
              </Tooltip>
            )}
            {pub.url && (
              <Tooltip title="View Online">
                <IconButton 
                  size="small" 
                  color="info"
                  href={pub.url}
                  target="_blank"
                  component="a"
                >
                  <LinkIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      </Card>
    </Fade>
  );

  const renderPublicationListItem = (pub, index) => (
    <Fade in={true} timeout={index * 100}>
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={getPublicationTypeIcon(pub.publication_type)}
                  label={pub.publication_type}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<DateIcon />}
                  label={pub.publication_year}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={pub.status}
                  size="small"
                  color={getStatusColor(pub.status)}
                />
              </Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {pub.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Author:</strong> {pub.author_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Publisher:</strong> {pub.publisher}
              </Typography>
              {pub.doi && (
                <Typography variant="caption" color="primary">
                  DOI: {pub.doi}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {pub.file_name && (
                  <Tooltip title="View PDF">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleViewPdf(pub.file_name)}
                    >
                      <PdfIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {pub.url && (
                  <Tooltip title="View Online">
                    <IconButton 
                      size="small" 
                      color="info"
                      href={pub.url}
                      target="_blank"
                      component="a"
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );

  const filterContent = (
    <Box sx={{ width: isMobile ? '100%' : 300, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={() => setFilterDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        label="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Title, author, publisher..."
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      {/* Publication Type Filter */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Publication Type</InputLabel>
        <Select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          label="Publication Type"
        >
          <MenuItem value="all">All Types</MenuItem>
          {getUniqueTypes().map(type => (
            <MenuItem key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          label="Status"
        >
          <MenuItem value="all">All Statuses</MenuItem>
          {getUniqueStatuses().map(status => (
            <MenuItem key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Year Filter */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Year</InputLabel>
        <Select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          label="Year"
        >
          <MenuItem value="all">All Years</MenuItem>
          {getUniqueYears().map(year => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Sort By */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          label="Sort By"
        >
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="oldest">Oldest First</MenuItem>
          <MenuItem value="year-desc">Year: Newest to Oldest</MenuItem>
          <MenuItem value="year-asc">Year: Oldest to Newest</MenuItem>
          <MenuItem value="title-asc">Title A-Z</MenuItem>
          <MenuItem value="title-desc">Title Z-A</MenuItem>
          <MenuItem value="author-asc">Author A-Z</MenuItem>
          <MenuItem value="author-desc">Author Z-A</MenuItem>
        </Select>
      </FormControl>

      {/* Reset Button */}
      <Button
        fullWidth
        variant="outlined"
        onClick={resetFilters}
        startIcon={<ClearIcon />}
        sx={{ mt: 1 }}
      >
        Reset All Filters
      </Button>
    </Box>
  );

  if (loading) {
    return (
      <>
        <NavigationMenu />
        <LoadingState />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <NavigationMenu />
      <Box component="main" sx={{ marginTop: 8, flexGrow: 1, p: 3 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Slide direction="down" in={true} mountOnEnter unmountOnExit>
            <Paper elevation={3} sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, #1976d2 0%, #1565c0 100%)`,
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    Publications Repository
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Browse and explore academic publications from researchers worldwide
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Total: {filteredPublications.length} publication{filteredPublications.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Tooltip title={viewMode === 'grid' ? 'Switch to List View' : 'Switch to Grid View'}>
                    <IconButton 
                      sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
                      onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    >
                      {viewMode === 'grid' ? <ViewListIcon /> : <GridViewIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Filter Publications">
                    <Badge badgeContent={searchTerm || selectedType !== 'all' || selectedStatus !== 'all' || selectedYear !== 'all' ? '!' : 0} color="error">
                      <IconButton 
                        sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
                        onClick={() => setFilterDrawerOpen(true)}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          </Slide>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Active Filters Display */}
          {(searchTerm || selectedType !== 'all' || selectedStatus !== 'all' || selectedYear !== 'all') && (
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {searchTerm && (
                <Chip
                  label={`Search: ${searchTerm}`}
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {selectedType !== 'all' && (
                <Chip
                  label={`Type: ${selectedType}`}
                  onDelete={() => setSelectedType('all')}
                  color="secondary"
                  variant="outlined"
                />
              )}
              {selectedStatus !== 'all' && (
                <Chip
                  label={`Status: ${selectedStatus}`}
                  onDelete={() => setSelectedStatus('all')}
                  color="info"
                  variant="outlined"
                />
              )}
              {selectedYear !== 'all' && (
                <Chip
                  label={`Year: ${selectedYear}`}
                  onDelete={() => setSelectedYear('all')}
                  color="warning"
                  variant="outlined"
                />
              )}
              <Chip
                label="Clear All"
                onDelete={resetFilters}
                color="error"
                variant="outlined"
              />
            </Box>
          )}

          {/* Publications Grid/List */}
          {filteredPublications.length === 0 ? (
            <Fade in={true}>
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <ArticleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No Publications Found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Try adjusting your search or filter criteria
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={resetFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear All Filters
                </Button>
              </Paper>
            </Fade>
          ) : viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {paginatedPublications.map((pub, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={pub.id}>
                  {renderPublicationCard(pub, index)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              {paginatedPublications.map((pub, index) => renderPublicationListItem(pub, index))}
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                size={isMobile ? 'small' : 'large'}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        {filterContent}
      </Drawer>

      {/* PDF Viewer Dialog */}
      <PDFViewer
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        pdfUrl={selectedPdfUrl}
        title="Publication PDF"
      />
    </Box>
  );
};

export default Publications;